// Builds the Invoice PDF client-side with jsPDF (same approach as the
// settlement statement in RegeneratePdfButton.jsx). Used by the "Email
// Invoice" and "Send Reminder" flows so the broker/customer gets a real,
// itemized invoice attached, not just an HTML email body. The automated
// 30-day reminder job has its own copy of this layout in
// supabase/functions/sendInvoiceReminders/index.ts -- it runs with no
// browser, so it can't import this file; keep the two in sync by hand.
import { invoiceChargeLines } from '@/lib/invoiceHelpers';

const money = (n) => (n < 0 ? '-' : '') + '$' + Math.abs(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(d);
};

export async function generateInvoicePdfBlob(invoice, companyName, load = null) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ format: 'letter', unit: 'pt' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const L = 48;
  const R = pageW - 48;
  const INK = [17, 17, 17];
  const MUTED = [110, 110, 110];
  const RULE = [210, 210, 210];
  let y = 54;

  const color = (c) => pdf.setTextColor(c[0], c[1], c[2]);
  const rule = (yy, c = RULE) => { pdf.setDrawColor(c[0], c[1], c[2]); pdf.setLineWidth(0.6); pdf.line(L, yy, R, yy); };
  const text = (str, x, yy, opts) => pdf.text(String(str ?? ''), x, yy, opts || {});
  const label = (str, x, yy) => { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(8); color(MUTED); text(String(str).toUpperCase(), x, yy); };
  const value = (str, x, yy, bold = false) => { pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(10); color(INK); text(str, x, yy); };
  const ensureSpace = (needed) => { if (y + needed > pageH - 60) { pdf.addPage(); y = 54; } };

  // ---- Header: company left, INVOICE + number/date right ----
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(18); color(INK);
  text(companyName || '', L, y);
  pdf.setFontSize(24); color(MUTED);
  text('INVOICE', R, y, { align: 'right' });
  y += 20;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); color(INK);
  text(`# ${invoice.invoice_number || ''}`, R, y, { align: 'right' });
  y += 14;
  color(MUTED);
  text(`Date: ${fmtDate(invoice.invoiced_date)}`, R, y, { align: 'right' });
  if (invoice.due_date) {
    y += 14;
    text(`Due: ${fmtDate(invoice.due_date)}${invoice.payment_terms && invoice.payment_terms !== 'Due on receipt' ? `  (${invoice.payment_terms})` : ''}`, R, y, { align: 'right' });
  }
  y += 22;
  rule(y);
  y += 22;

  // ---- Bill To (left) / Reference (right) ----
  const colB = L + 300;
  label('Bill To', L, y);
  label('Reference', colB, y);
  y += 15;
  value(invoice.customer_name || '—', L, y, true);
  const refRows = [
    invoice.load_number ? ['Load #', `#${invoice.load_number}`] : null,
    invoice.custom_invoice_number ? ['Broker Ref #', invoice.custom_invoice_number] : null,
  ].filter(Boolean);
  let ry = y;
  for (const [k, v] of refRows) {
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10); color(MUTED); text(k, colB, ry);
    color(INK); pdf.setFont('helvetica', 'bold'); text(v, colB + 80, ry);
    ry += 14;
  }
  let by = y + 14;
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); color(MUTED);
  for (const line of String(invoice.customer_address || '').split('\n').map(l => l.trim()).filter(Boolean)) {
    text(line, L, by);
    by += 12;
  }
  if (invoice.customer_email) {
    text(invoice.customer_email, L, by);
    by += 13;
  }
  y = Math.max(by, ry) + 14;

  // ---- Shipment details (only what the load actually has) ----
  if (load) {
    const route = [load.origin, load.destination].filter(Boolean).join('  to  ');
    const facts = [
      ['Route', route],
      ['Pickup', fmtDate(load.pickup_date)],
      ['Delivery', fmtDate(load.delivery_date)],
      ['Miles', load.miles ? `${Math.round(Number(load.miles)).toLocaleString()} mi` : ''],
      ['Equipment', load.equipment_required || ''],
      ['Commodity', load.commodity || ''],
    ].filter(([, v]) => v);
    if (facts.length) {
      rule(y);
      y += 18;
      label('Shipment', L, y);
      y += 15;
      const half = R - L;
      // Route runs full width on its own line; the rest sit in a 3-column grid.
      const [routeRow, ...rest] = facts[0][0] === 'Route' ? facts : [null, ...facts];
      if (routeRow) {
        pdf.setFont('helvetica', 'bold'); pdf.setFontSize(10); color(INK);
        const wrapped = pdf.splitTextToSize(routeRow[1], half);
        text(wrapped, L, y);
        y += wrapped.length * 13 + 6;
      }
      const cellW = (R - L) / 3;
      rest.forEach(([k, v], i) => {
        const cx = L + (i % 3) * cellW;
        if (i % 3 === 0 && i > 0) y += 30;
        label(k, cx, y);
        value(v, cx, y + 13);
      });
      if (rest.length) y += 30;
      y += 4;
    }
  }

  // ---- Charges table ----
  ensureSpace(90);
  y += 4;
  pdf.setFillColor(243, 243, 243);
  pdf.rect(L, y - 12, R - L, 22, 'F');
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(9); color(MUTED);
  text('DESCRIPTION', L + 8, y + 2);
  text('AMOUNT', R - 8, y + 2, { align: 'right' });
  y += 24;

  const charges = invoiceChargeLines(load, invoice.total_amount, invoice.line_items);
  pdf.setFontSize(10);
  for (const c of charges) {
    ensureSpace(24);
    pdf.setFont('helvetica', 'normal'); color(INK);
    text(c.label, L + 8, y);
    text(money(c.amount), R - 8, y, { align: 'right' });
    y += 8;
    rule(y);
    y += 16;
  }

  // ---- Totals (right-aligned block) ----
  ensureSpace(90);
  const tx = R - 210;
  const totalRow = (k, v, bold = false, c = INK) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal'); pdf.setFontSize(bold ? 11 : 10); color(c);
    text(k, tx, y);
    text(v, R - 8, y, { align: 'right' });
    y += 17;
  };
  y += 2;
  totalRow('Total', money(invoice.total_amount), true);
  const payments = Array.isArray(invoice.payments) ? invoice.payments : [];
  for (const p of payments) {
    totalRow(`Payment ${fmtDate(p.date)}${p.reference ? ` (${p.reference})` : ''}`, money(-(p.amount || 0)), false, MUTED);
  }
  y += 2;
  pdf.setFillColor(243, 243, 243);
  pdf.rect(tx - 8, y - 14, R - tx + 8, 26, 'F');
  pdf.setFont('helvetica', 'bold'); pdf.setFontSize(12); color(INK);
  text('BALANCE DUE', tx, y + 3);
  text(money(invoice.balance_due), R - 8, y + 3, { align: 'right' });
  y += 34;

  // ---- Notes ----
  if (invoice.notes) {
    ensureSpace(50);
    label('Notes', L, y);
    y += 14;
    pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); color(INK);
    const lines = pdf.splitTextToSize(String(invoice.notes), R - L);
    text(lines, L, y);
    y += lines.length * 12 + 10;
  }

  // ---- Footer ----
  pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); color(MUTED);
  rule(pageH - 56);
  text(`Thank you for your business.  —  ${companyName || ''}`, L, pageH - 40);

  return pdf.output('blob');
}

export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(',')[1] || '');
    reader.onerror = () => reject(reader.error || new Error('Failed to read PDF blob'));
    reader.readAsDataURL(blob);
  });
}

export async function generateInvoicePdfBase64(invoice, companyName, load = null) {
  const blob = await generateInvoicePdfBlob(invoice, companyName, load);
  return blobToBase64(blob);
}
